import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Plus,
  Edit2,
  Trash2,
  Settings,
  Package,
  Users,
  Loader2,
  RefreshCw,
  Tag,
  Wrench,
  Disc,
  AlertCircle,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { SearchableDropdownBox } from '@/components/SearchableDropdownBox';
import { formatSpecificationDisplay } from '@/utils/specificationDisplay';

const MasterDataPage = () => {
  const { user } = useAuth();
  const isSupervisor = user?.role === 'SUPERVISOR';
  const [activeTab, setActiveTab] = useState('machines');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [machines, setMachines] = useState([]);
  const [parts, setParts] = useState([]);
  const [operators, setOperators] = useState([]);
  const [derivatives, setDerivatives] = useState([]);
  const [toolMasters, setToolMasters] = useState([]);
  const [insertTypes, setInsertTypes] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [downtimeReasons, setDowntimeReasons] = useState([]);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [machineForm, setMachineForm] = useState({ code: '', name: '', active: true });
  const resequencePartSpecs = (rows) =>
    (rows || []).map((r, idx) => ({ ...r, sequence: String(idx) }));
  const emptyPartSpecRow = () => ({
    parameter: '',
    specification: '',
    pieces_per_interval: '1',
    sequence: '0',
  });
  const [partForm, setPartForm] = useState({
    part_no: '',
    drawing_no: '',
    description: '',
    parameter_specs: [emptyPartSpecRow()],
  });
  const [operatorForm, setOperatorForm] = useState({ name: '', badge_id: '' });
  const [derivativeForm, setDerivativeForm] = useState({ code: '', description: '' });
  const [toolMasterForm, setToolMasterForm] = useState({ tool_code: '', tool_description: '' });
  const [insertTypeForm, setInsertTypeForm] = useState({ insert_type_code: '', insert_type_description: '', total_phases: 6 });
  const [rejectionReasonForm, setRejectionReasonForm] = useState({ code: '', description: '' });
  const [downtimeReasonForm, setDowntimeReasonForm] = useState({ code: '', description: '' });
  const [copySpecsSourcePartId, setCopySpecsSourcePartId] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [machinesRes, partsRes, operatorsRes, derivativesRes, toolMastersRes, insertTypesRes, rejectionReasonsRes, downtimeReasonsRes] = await Promise.all([
        api.get('/machines'),
        api.get('/parts'),
        api.get('/operators'),
        api.get('/derivatives'),
        api.get('/tool-master'),
        api.get('/insert-type-master'),
        api.get('/rejection-reasons'),
        api.get('/downtime-reasons')
      ]);
      setMachines(machinesRes.data);
      setParts(partsRes.data);
      setOperators(operatorsRes.data);
      setDerivatives(derivativesRes.data);
      setToolMasters(toolMastersRes.data);
      setInsertTypes(insertTypesRes.data);
      setRejectionReasons(rejectionReasonsRes.data);
      setDowntimeReasons(downtimeReasonsRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreateDialog = () => {
    setDialogMode('create');
    setEditingItem(null);
    resetForms();
    setCopySpecsSourcePartId('');
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setDialogMode('edit');
    setEditingItem(item);
    setCopySpecsSourcePartId('');
    if (activeTab === 'machines') {
      setMachineForm({ code: item.code, name: item.name, active: item.active });
    } else if (activeTab === 'parts') {
      const specs = Array.isArray(item.parameter_specs) && item.parameter_specs.length > 0
        ? resequencePartSpecs(item.parameter_specs.map((s) => ({
            parameter: s.parameter ?? '',
            specification: formatSpecificationDisplay(s.specification ?? ''),
            pieces_per_interval:
              s.pieces_per_interval == null || s.pieces_per_interval === '' ? '1' : String(s.pieces_per_interval),
            sequence: s.sequence == null || s.sequence === '' ? '' : String(s.sequence),
          })))
        : [emptyPartSpecRow()];
      setPartForm({ part_no: item.part_no, drawing_no: item.drawing_no, description: item.description, parameter_specs: specs });
    } else if (activeTab === 'operators') {
      setOperatorForm({ name: item.name, badge_id: item.badge_id });
    } else if (activeTab === 'derivatives') {
      setDerivativeForm({ code: item.code, description: item.description || '' });
    } else if (activeTab === 'toolMasters') {
      setToolMasterForm({ tool_code: item.tool_code, tool_description: item.tool_description || '' });
    } else if (activeTab === 'insertTypes') {
      setInsertTypeForm({ insert_type_code: item.insert_type_code, insert_type_description: item.insert_type_description || '', total_phases: item.total_phases ?? 6 });
    } else if (activeTab === 'rejectionReasons') {
      setRejectionReasonForm({ code: item.code, description: item.description || '' });
    } else if (activeTab === 'downtimeReasons') {
      setDowntimeReasonForm({ code: item.code, description: item.description || '' });
    }
    setDialogOpen(true);
  };

  const resetForms = () => {
    setMachineForm({ code: '', name: '', active: true });
    setPartForm({ part_no: '', drawing_no: '', description: '', parameter_specs: [emptyPartSpecRow()] });
    setOperatorForm({ name: '', badge_id: '' });
    setDerivativeForm({ code: '', description: '' });
    setToolMasterForm({ tool_code: '', tool_description: '' });
    setInsertTypeForm({ insert_type_code: '', insert_type_description: '', total_phases: 6 });
    setRejectionReasonForm({ code: '', description: '' });
    setDowntimeReasonForm({ code: '', description: '' });
  };

  const applyParameterSpecsFromPart = () => {
    if (!copySpecsSourcePartId) {
      toast.error('Choose a part to copy specs from.');
      return;
    }
    const src = parts.find((p) => p.id === copySpecsSourcePartId);
    if (!src) {
      toast.error('Part not found. Try refreshing the list.');
      return;
    }
    const rows = (Array.isArray(src.parameter_specs) ? src.parameter_specs : [])
      .filter((s) => s && typeof s === 'object')
      .map((s) => ({
        parameter: String(s.parameter ?? '').trim(),
        specification: String(s.specification ?? '').trim(),
        pieces_per_interval:
          s.pieces_per_interval == null || s.pieces_per_interval === '' ? '1' : String(s.pieces_per_interval),
        sequence: s.sequence == null || s.sequence === '' ? '' : String(s.sequence),
      }))
      .filter((r) => r.parameter && r.specification);
    if (rows.length === 0) {
      toast.error('That part has no parameter specs to copy.');
      return;
    }
    const copied = rows.map((r) => ({ ...r }));
    const existingComplete = (partForm.parameter_specs || [])
      .map((r) => ({
        parameter: String(r.parameter ?? '').trim(),
        specification: String(r.specification ?? '').trim(),
        pieces_per_interval:
          r.pieces_per_interval == null || r.pieces_per_interval === '' ? '1' : String(r.pieces_per_interval),
        sequence: r.sequence == null || r.sequence === '' ? '' : String(r.sequence),
      }))
      .filter((r) => r.parameter && r.specification);
    const merged =
      existingComplete.length > 0
        ? [...existingComplete.map((r) => ({ ...r })), ...copied]
        : copied;
    setPartForm((prev) => ({ ...prev, parameter_specs: resequencePartSpecs(merged) }));
    toast.success(
      existingComplete.length > 0
        ? `Appended ${rows.length} spec row(s) from ${src.part_no}.`
        : `Copied ${rows.length} spec row(s) from ${src.part_no}.`
    );
  };

  const handleSave = async () => {
    try {
      if (activeTab === 'machines') {
        if (!machineForm.code?.trim()) {
          toast.error('Machine Code is required');
          return;
        }
        if (!machineForm.name?.trim()) {
          toast.error('Machine Name is required');
          return;
        }
        if (dialogMode === 'create') {
          await api.post('/machines', machineForm);
          toast.success('Machine created');
        } else {
          await api.put(`/machines/${editingItem.id}`, machineForm);
          toast.success('Machine updated');
        }
      } else if (activeTab === 'parts') {
        if (!partForm.part_no?.trim()) {
          toast.error('Part Number is required');
          return;
        }
        if (!partForm.drawing_no?.trim()) {
          toast.error('Drawing Number is required');
          return;
        }
        if (!partForm.description?.trim()) {
          toast.error('Description is required');
          return;
        }
        const specRows = (partForm.parameter_specs || []).map((r, idx) => ({
          parameter: (r.parameter || '').trim(),
          specification: (r.specification || '').trim(),
          pieces_per_interval:
            r.pieces_per_interval == null || r.pieces_per_interval === '' ? 1 : Number(r.pieces_per_interval),
          sequence: idx,
        }));
        for (const r of specRows) {
          if (r.parameter || r.specification) {
            if (!r.parameter || !r.specification) {
              toast.error('Each parameter row must have both name and specification; specification cannot be empty.');
              return;
            }
            if (Number.isNaN(r.pieces_per_interval) || r.pieces_per_interval <= 0) {
              toast.error('Pieces must be a positive number.');
              return;
            }
          }
        }
        const partPayload = {
          part_no: partForm.part_no,
          drawing_no: partForm.drawing_no,
          description: partForm.description,
          parameter_specs: specRows.filter((r) => r.parameter && r.specification),
        };
        if (dialogMode === 'create') {
          await api.post('/parts', partPayload);
          toast.success('Part created');
        } else {
          await api.put(`/parts/${editingItem.id}`, partPayload);
          toast.success('Part updated');
        }
      } else if (activeTab === 'operators') {
        if (!operatorForm.name?.trim()) {
          toast.error('Operator Name is required');
          return;
        }
        if (!operatorForm.badge_id?.trim()) {
          toast.error('Badge ID is required');
          return;
        }
        if (dialogMode === 'create') {
          await api.post('/operators', operatorForm);
          toast.success('Operator created');
        } else {
          await api.put(`/operators/${editingItem.id}`, operatorForm);
          toast.success('Operator updated');
        }
      } else if (activeTab === 'derivatives') {
        if (!derivativeForm.code?.trim()) {
          toast.error('Derivative Code is required');
          return;
        }
        if (!derivativeForm.description?.trim()) {
          toast.error('Description is required');
          return;
        }
        if (dialogMode === 'create') {
          await api.post('/derivatives', derivativeForm);
          toast.success('Derivative created');
        } else {
          await api.put(`/derivatives/${editingItem.id}`, derivativeForm);
          toast.success('Derivative updated');
        }
      } else if (activeTab === 'toolMasters') {
        if (!toolMasterForm.tool_code?.trim()) {
          toast.error('Tool Code is required');
          return;
        }
        if (!toolMasterForm.tool_description?.trim()) {
          toast.error('Description is required');
          return;
        }
        if (dialogMode === 'create') {
          await api.post('/tool-master', toolMasterForm);
          toast.success('Tool created');
        } else {
          await api.put(`/tool-master/${editingItem.id}`, toolMasterForm);
          toast.success('Tool updated');
        }
      } else if (activeTab === 'insertTypes') {
        if (!insertTypeForm.insert_type_code?.trim()) {
          toast.error('Insert Type Code is required');
          return;
        }
        if (!insertTypeForm.insert_type_description?.trim()) {
          toast.error('Description is required');
          return;
        }
        const phases = insertTypeForm.total_phases ?? 6;
        if (phases < 1 || phases > 24) {
          toast.error('Total Phases is required (1–24)');
          return;
        }
        if (dialogMode === 'create') {
          await api.post('/insert-type-master', insertTypeForm);
          toast.success('Insert type created');
        } else {
          await api.put(`/insert-type-master/${editingItem.id}`, insertTypeForm);
          toast.success('Insert type updated');
        }
      } else if (activeTab === 'rejectionReasons') {
        if (!rejectionReasonForm.code?.trim()) {
          toast.error('Code is required');
          return;
        }
        if (dialogMode === 'create') {
          await api.post('/rejection-reasons', rejectionReasonForm);
          toast.success('Rejection reason created');
        } else {
          await api.put(`/rejection-reasons/${editingItem.id}`, rejectionReasonForm);
          toast.success('Rejection reason updated');
        }
      } else if (activeTab === 'downtimeReasons') {
        if (!downtimeReasonForm.code?.trim()) {
          toast.error('Code is required');
          return;
        }
        if (dialogMode === 'create') {
          await api.post('/downtime-reasons', downtimeReasonForm);
          toast.success('Downtime reason created');
        } else {
          await api.put(`/downtime-reasons/${editingItem.id}`, downtimeReasonForm);
          toast.success('Downtime reason updated');
        }
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const endpointMap = {
        toolMasters: 'tool-master',
        insertTypes: 'insert-type-master',
        rejectionReasons: 'rejection-reasons',
        downtimeReasons: 'downtime-reasons'
      };
      const endpoint = endpointMap[type] || type;
      await api.delete(`/${endpoint}/${id}`);
      toast.success('Item deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleToggleActive = async (machineId) => {
    try {
      await api.patch(`/machines/${machineId}/toggle-active`);
      toast.success('Machine status updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const getDialogTitle = () => {
    const action = dialogMode === 'create' ? 'Add New' : 'Edit';
    const typeMap = {
      machines: 'Machine',
      parts: 'Part',
      operators: 'Operator',
      derivatives: 'Derivative',
      toolMasters: 'Tool',
      insertTypes: 'Insert Type',
      rejectionReasons: 'Rejection Reason',
      downtimeReasons: 'Downtime Reason'
    };
    return `${action} ${typeMap[activeTab]}`;
  };

  const partSpecSourceOptions = parts.filter((p) => p.id !== editingItem?.id);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="master-data-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Master Data</h1>
          <p className="text-slate-500">Manage machines, parts, operators, derivatives, tools, insert types, rejection & downtime reasons</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-master-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {!isSupervisor && (
            <Button onClick={openCreateDialog} data-testid="add-new-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add New
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex h-11 w-full flex-nowrap gap-px p-1">
          <TabsTrigger value="machines" className="flex-1 min-w-0 flex items-center justify-center gap-1 text-xs sm:text-sm" data-testid="tab-machines">
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Machines</span>
            <span className="sm:hidden">M</span>
            ({machines.length})
          </TabsTrigger>
          <TabsTrigger value="parts" className="flex-1 min-w-0 flex items-center justify-center gap-1 text-xs sm:text-sm" data-testid="tab-parts">
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Parts</span>
            <span className="sm:hidden">P</span>
            ({parts.length})
          </TabsTrigger>
          <TabsTrigger value="operators" className="flex-1 min-w-0 flex items-center justify-center gap-1 text-xs sm:text-sm" data-testid="tab-operators">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Operators</span>
            <span className="sm:hidden">O</span>
            ({operators.length})
          </TabsTrigger>
          <TabsTrigger value="derivatives" className="flex-1 min-w-0 flex items-center justify-center gap-1 text-xs sm:text-sm" data-testid="tab-derivatives">
            <Tag className="w-4 h-4" />
            <span className="hidden sm:inline">Derivatives</span>
            <span className="sm:hidden">D</span>
            ({derivatives.length})
          </TabsTrigger>
          <TabsTrigger value="toolMasters" className="flex-1 min-w-0 flex items-center justify-center gap-1 text-xs sm:text-sm" data-testid="tab-tool-masters">
            <Wrench className="w-4 h-4" />
            <span className="hidden sm:inline">Tools</span>
            <span className="sm:hidden">T</span>
            ({toolMasters.length})
          </TabsTrigger>
          <TabsTrigger value="insertTypes" className="flex-1 min-w-0 flex items-center justify-center gap-1 text-xs sm:text-sm" data-testid="tab-insert-types">
            <Disc className="w-4 h-4" />
            <span className="hidden sm:inline">Insert Types</span>
            <span className="sm:hidden">I</span>
            ({insertTypes.length})
          </TabsTrigger>
          <TabsTrigger value="rejectionReasons" className="flex-1 min-w-0 flex items-center justify-center gap-1 text-xs sm:text-sm" data-testid="tab-rejection-reasons">
            <AlertCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Rejection</span>
            <span className="sm:hidden">RR</span>
            ({rejectionReasons.length})
          </TabsTrigger>
          <TabsTrigger value="downtimeReasons" className="flex-1 min-w-0 flex items-center justify-center gap-1 text-xs sm:text-sm" data-testid="tab-downtime-reasons">
            <Clock className="w-4 h-4" />
            <span className="hidden sm:inline">Downtime</span>
            <span className="sm:hidden">DR</span>
            ({downtimeReasons.length})
          </TabsTrigger>
        </TabsList>

        {/* Machines Tab */}
        <TabsContent value="machines" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>CNC Machines</CardTitle>
              <CardDescription>Manage your CNC machine inventory</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    {!isSupervisor && <TableHead className="w-28">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 3 : 4} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : machines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 3 : 4} className="text-center py-8 text-slate-500">
                        No machines found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    machines.map((machine) => (
                      <TableRow key={machine.id} data-testid={`machine-row-${machine.id}`}>
                        <TableCell className="font-mono font-medium">{machine.code}</TableCell>
                        <TableCell>{machine.name}</TableCell>
                        <TableCell>
                          {isSupervisor ? (
                            <Switch
                              checked={machine.active}
                              onCheckedChange={() => handleToggleActive(machine.id)}
                            />
                          ) : (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${machine.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                              {machine.active ? 'Active' : 'Inactive'}
                            </span>
                          )}
                        </TableCell>
                        {!isSupervisor && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(machine)} className="h-9 w-9">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(machine.id, 'machines')} className="h-9 w-9 text-red-500 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Parts Tab */}
        <TabsContent value="parts" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Parts</CardTitle>
              <CardDescription>Manage part numbers and drawings</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Part No</TableHead>
                    <TableHead>Drawing No</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Parameters</TableHead>
                    {!isSupervisor && <TableHead className="w-28">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 4 : 5} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : parts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 4 : 5} className="text-center py-8 text-slate-500">
                        No parts found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    parts.map((part) => (
                      <TableRow key={part.id} data-testid={`part-row-${part.id}`}>
                        <TableCell className="font-mono font-medium">{part.part_no}</TableCell>
                        <TableCell className="font-mono">{part.drawing_no}</TableCell>
                        <TableCell>{part.description}</TableCell>
                        <TableCell
                          className="max-w-[12rem] truncate text-xs text-slate-600"
                          title={
                            Array.isArray(part.parameter_specs) && part.parameter_specs.length > 0
                              ? part.parameter_specs
                                  .map((s) => `${s.parameter}: ${formatSpecificationDisplay(s.specification)}`)
                                  .join('\n')
                              : undefined
                          }
                        >
                          {!part.parameter_specs?.length
                            ? '—'
                            : `${part.parameter_specs.length} spec(s)`}
                        </TableCell>
                        {!isSupervisor && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(part)} className="h-9 w-9">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(part.id, 'parts')} className="h-9 w-9 text-red-500 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Operators Tab */}
        <TabsContent value="operators" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Operators</CardTitle>
              <CardDescription>Manage machine operators</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Name</TableHead>
                    <TableHead>Badge ID</TableHead>
                    {!isSupervisor && <TableHead className="w-28">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 2 : 3} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : operators.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 2 : 3} className="text-center py-8 text-slate-500">
                        No operators found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    operators.map((op) => (
                      <TableRow key={op.id} data-testid={`operator-row-${op.id}`}>
                        <TableCell className="font-medium">{op.name}</TableCell>
                        <TableCell className="font-mono">{op.badge_id || '-'}</TableCell>
                        {!isSupervisor && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(op)} className="h-9 w-9">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(op.id, 'operators')} className="h-9 w-9 text-red-500 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Derivatives Tab */}
        <TabsContent value="derivatives" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Derivatives</CardTitle>
              <CardDescription>Manage bearing variations (e.g., Z, ZZ, P, RO, CR, N)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Code</TableHead>
                    <TableHead>Description</TableHead>
                    {!isSupervisor && <TableHead className="w-28">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 2 : 3} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : derivatives.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 2 : 3} className="text-center py-8 text-slate-500">
                        No derivatives found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    derivatives.map((deriv) => (
                      <TableRow key={deriv.id} data-testid={`derivative-row-${deriv.id}`}>
                        <TableCell className="font-mono font-medium">{deriv.code}</TableCell>
                        <TableCell>{deriv.description || '-'}</TableCell>
                        {!isSupervisor && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(deriv)} className="h-9 w-9">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(deriv.id, 'derivatives')} className="h-9 w-9 text-red-500 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tool Masters Tab */}
        <TabsContent value="toolMasters" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Tool Masters</CardTitle>
              <CardDescription>Manage tool codes used in Tool Life Monitoring (e.g., F, T, G)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Tool Code</TableHead>
                    <TableHead>Description</TableHead>
                    {!isSupervisor && <TableHead className="w-28">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 2 : 3} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : toolMasters.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 2 : 3} className="text-center py-8 text-slate-500">
                        No tools found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    toolMasters.map((tool) => (
                      <TableRow key={tool.id} data-testid={`tool-row-${tool.id}`}>
                        <TableCell className="font-mono font-medium">{tool.tool_code}</TableCell>
                        <TableCell>{tool.tool_description || '-'}</TableCell>
                        {!isSupervisor && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(tool)} className="h-9 w-9">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(tool.id, 'toolMasters')} className="h-9 w-9 text-red-500 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insert Types Tab */}
        <TabsContent value="insertTypes" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Insert Types</CardTitle>
              <CardDescription>Manage insert type codes used in Tool Life Monitoring (e.g., TNMG, VNMG)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Insert Type Code</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-24">Total Phases</TableHead>
                    {!isSupervisor && <TableHead className="w-28">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 3 : 4} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : insertTypes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 3 : 4} className="text-center py-8 text-slate-500">
                        No insert types found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    insertTypes.map((it) => (
                      <TableRow key={it.id} data-testid={`insert-type-row-${it.id}`}>
                        <TableCell className="font-mono font-medium">{it.insert_type_code}</TableCell>
                        <TableCell>{it.insert_type_description || '-'}</TableCell>
                        <TableCell className="font-mono">{it.total_phases ?? 6}</TableCell>
                        {!isSupervisor && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(it)} className="h-9 w-9">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(it.id, 'insertTypes')} className="h-9 w-9 text-red-500 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rejection Reasons Tab */}
        <TabsContent value="rejectionReasons" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Rejection</CardTitle>
              <CardDescription>Reasons for rejected parts (e.g., MF, Bore, Dimension)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Code</TableHead>
                    <TableHead>Description</TableHead>
                    {!isSupervisor && <TableHead className="w-28">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 2 : 3} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : rejectionReasons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 2 : 3} className="text-center py-8 text-slate-500">
                        No rejection reasons found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rejectionReasons.map((rr) => (
                      <TableRow key={rr.id} data-testid={`rejection-reason-row-${rr.id}`}>
                        <TableCell className="font-mono font-medium">{rr.code}</TableCell>
                        <TableCell>{rr.description || '-'}</TableCell>
                        {!isSupervisor && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(rr)} className="h-9 w-9">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(rr.id, 'rejectionReasons')} className="h-9 w-9 text-red-500 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Downtime Reasons Tab */}
        <TabsContent value="downtimeReasons" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Downtime</CardTitle>
              <CardDescription>Reasons for machine downtime (e.g., Breakdown, Setup, Maintenance)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Code</TableHead>
                    <TableHead>Description</TableHead>
                    {!isSupervisor && <TableHead className="w-28">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 2 : 3} className="text-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : downtimeReasons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisor ? 2 : 3} className="text-center py-8 text-slate-500">
                        No downtime reasons found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    downtimeReasons.map((dr) => (
                      <TableRow key={dr.id} data-testid={`downtime-reason-row-${dr.id}`}>
                        <TableCell className="font-mono font-medium">{dr.code}</TableCell>
                        <TableCell>{dr.description || '-'}</TableCell>
                        {!isSupervisor && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(dr)} className="h-9 w-9">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(dr.id, 'downtimeReasons')} className="h-9 w-9 text-red-500 hover:text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={activeTab === 'parts' ? 'sm:max-w-lg max-h-[90vh] overflow-y-auto' : 'sm:max-w-md'} data-testid="master-data-dialog">
          <DialogHeader>
            <DialogTitle>{getDialogTitle()}</DialogTitle>
            <DialogDescription>
              {dialogMode === 'create' ? 'Fill in the details below.' : 'Update the details below.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {activeTab === 'machines' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="machine-code">Machine Code *</Label>
                  <Input
                    id="machine-code"
                    value={machineForm.code}
                    onChange={(e) => setMachineForm({ ...machineForm, code: e.target.value })}
                    placeholder="e.g., CNC-15"
                    className="h-11"
                    data-testid="input-machine-code"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="machine-name">Machine Name *</Label>
                  <Input
                    id="machine-name"
                    value={machineForm.name}
                    onChange={(e) => setMachineForm({ ...machineForm, name: e.target.value })}
                    placeholder="e.g., CNC Machine 15"
                    className="h-11"
                    data-testid="input-machine-name"
                    required
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="machine-active">Active Status *</Label>
                  <Switch
                    id="machine-active"
                    checked={machineForm.active}
                    onCheckedChange={(checked) => setMachineForm({ ...machineForm, active: checked })}
                    data-testid="input-machine-active"
                  />
                </div>
              </>
            )}

            {activeTab === 'parts' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="part-no">Part Number *</Label>
                  <Input
                    id="part-no"
                    value={partForm.part_no}
                    onChange={(e) => setPartForm({ ...partForm, part_no: e.target.value })}
                    placeholder="e.g., 6312 OK"
                    className="h-11"
                    data-testid="input-part-no"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="drawing-no">Drawing Number *</Label>
                  <Input
                    id="drawing-no"
                    value={partForm.drawing_no}
                    onChange={(e) => setPartForm({ ...partForm, drawing_no: e.target.value })}
                    placeholder="e.g., DWG-6312"
                    className="h-11"
                    data-testid="input-drawing-no"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    value={partForm.description}
                    onChange={(e) => setPartForm({ ...partForm, description: e.target.value })}
                    placeholder="e.g., Ball Bearing 6312"
                    className="h-11"
                    data-testid="input-description"
                    required
                  />
                </div>
                <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50/60 p-3">
                  <Label className="text-sm font-medium">Copy specs from another part</Label>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                    <div className="min-w-0 flex-1">
                      <SearchableDropdownBox
                        options={partSpecSourceOptions.map((p) => ({
                          value: p.id,
                          label: p.part_no,
                        }))}
                        value={copySpecsSourcePartId}
                        onChange={(v) => setCopySpecsSourcePartId(v == null ? '' : String(v))}
                        placeholder="Choose a part…"
                        searchPlaceholder="Search part no."
                        emptyMessage="No matching parts."
                        disabled={partSpecSourceOptions.length === 0}
                        triggerClassName="w-full bg-background"
                        triggerTestId="copy-specs-from-select"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="h-9 shrink-0 sm:w-auto"
                      onClick={applyParameterSpecsFromPart}
                      disabled={!copySpecsSourcePartId || partSpecSourceOptions.length === 0}
                      data-testid="copy-specs-apply-btn"
                    >
                      Apply to form
                    </Button>
                  </div>
                  {partSpecSourceOptions.length === 0 ? (
                    <p className="text-xs text-slate-500">
                      No other parts to copy from. Add another part first, or refresh the list.
                    </p>
                  ) : (
                    <p className="text-xs text-slate-500">
                      If you already have complete parameter rows below, new rows are appended. Otherwise the list is filled from the selected part. You can edit before saving.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label>Parameters &amp; specifications</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() =>
                        setPartForm({
                          ...partForm,
                          parameter_specs: resequencePartSpecs([
                            ...(partForm.parameter_specs || []),
                            emptyPartSpecRow(),
                          ]),
                        })
                      }
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      Add row
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Each row: parameter, specification, and pieces (used for multi-value 5× in-process capture).
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto rounded-md border border-slate-200 p-2">
                    {(partForm.parameter_specs || []).map((row, idx) => (
                      <div key={idx} className="flex flex-wrap items-end gap-2">
                        <div className="space-y-1 flex-1 min-w-[8rem]">
                          <Label className="text-xs text-slate-600">Parameter</Label>
                          <Input
                            value={row.parameter}
                            onChange={(e) => {
                              const next = [...(partForm.parameter_specs || [])];
                              next[idx] = { ...next[idx], parameter: e.target.value };
                              setPartForm({ ...partForm, parameter_specs: next });
                            }}
                            placeholder="e.g. OD"
                            className="h-9 text-sm"
                            data-testid={`part-spec-parameter-${idx}`}
                          />
                        </div>
                        <div className="space-y-1 flex-1 min-w-[10rem]">
                          <Label className="text-xs text-slate-600">Specification</Label>
                          <Input
                            value={row.specification}
                            onChange={(e) => {
                              const next = [...(partForm.parameter_specs || [])];
                              next[idx] = { ...next[idx], specification: e.target.value };
                              setPartForm({ ...partForm, parameter_specs: next });
                            }}
                            placeholder="e.g. 32 ± 0.05"
                            className="h-9 text-sm"
                            data-testid={`part-spec-specification-${idx}`}
                          />
                        </div>
                        <div className="space-y-1 w-28">
                          <Label className="text-xs text-slate-600">Pieces</Label>
                          <Input
                            type="number"
                            min={1}
                            value={row.pieces_per_interval ?? '1'}
                            onChange={(e) => {
                              const next = [...(partForm.parameter_specs || [])];
                              next[idx] = { ...next[idx], pieces_per_interval: e.target.value };
                              setPartForm({ ...partForm, parameter_specs: next });
                            }}
                            className="h-9 text-sm"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 shrink-0 text-slate-400 hover:text-red-600"
                          disabled={(partForm.parameter_specs || []).length <= 1}
                          onClick={() => {
                            const next = (partForm.parameter_specs || []).filter((_, i) => i !== idx);
                            setPartForm({
                              ...partForm,
                              parameter_specs: resequencePartSpecs(next.length ? next : [emptyPartSpecRow()]),
                            });
                          }}
                          aria-label="Remove row"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'operators' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="op-name">Operator Name *</Label>
                  <Input
                    id="op-name"
                    value={operatorForm.name}
                    onChange={(e) => setOperatorForm({ ...operatorForm, name: e.target.value })}
                    placeholder="e.g., Rajesh Kumar"
                    className="h-11"
                    data-testid="input-operator-name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="badge-id">Badge ID *</Label>
                  <Input
                    id="badge-id"
                    value={operatorForm.badge_id}
                    onChange={(e) => setOperatorForm({ ...operatorForm, badge_id: e.target.value })}
                    placeholder="e.g., OP-009"
                    className="h-11"
                    data-testid="input-badge-id"
                    required
                  />
                </div>
              </>
            )}

            {activeTab === 'derivatives' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="deriv-code">Derivative Code *</Label>
                  <Input
                    id="deriv-code"
                    value={derivativeForm.code}
                    onChange={(e) => setDerivativeForm({ ...derivativeForm, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., ZZ, SS, MF"
                    className="h-11"
                    data-testid="input-derivative-code"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deriv-desc">Description *</Label>
                  <Input
                    id="deriv-desc"
                    value={derivativeForm.description}
                    onChange={(e) => setDerivativeForm({ ...derivativeForm, description: e.target.value })}
                    placeholder="e.g., Double Shield"
                    className="h-11"
                    data-testid="input-derivative-description"
                    required
                  />
                </div>
              </>
            )}

            {activeTab === 'toolMasters' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="tool-code">Tool Code *</Label>
                  <Input
                    id="tool-code"
                    value={toolMasterForm.tool_code}
                    onChange={(e) => setToolMasterForm({ ...toolMasterForm, tool_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., F, T, G"
                    className="h-11"
                    data-testid="input-tool-code"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tool-desc">Description *</Label>
                  <Input
                    id="tool-desc"
                    value={toolMasterForm.tool_description}
                    onChange={(e) => setToolMasterForm({ ...toolMasterForm, tool_description: e.target.value })}
                    placeholder="e.g., Facing Tool"
                    className="h-11"
                    data-testid="input-tool-description"
                    required
                  />
                </div>
              </>
            )}

            {activeTab === 'insertTypes' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="insert-code">Insert Type Code *</Label>
                  <Input
                    id="insert-code"
                    value={insertTypeForm.insert_type_code}
                    onChange={(e) => setInsertTypeForm({ ...insertTypeForm, insert_type_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., TNMG, VNMG"
                    className="h-11"
                    data-testid="input-insert-type-code"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="insert-desc">Description *</Label>
                  <Input
                    id="insert-desc"
                    value={insertTypeForm.insert_type_description}
                    onChange={(e) => setInsertTypeForm({ ...insertTypeForm, insert_type_description: e.target.value })}
                    placeholder="e.g., Triangular Negative Insert"
                    className="h-11"
                    data-testid="input-insert-type-description"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="insert-total-phases">Total Phases *</Label>
                  <Input
                    id="insert-total-phases"
                    type="number"
                    min={1}
                    max={24}
                    value={insertTypeForm.total_phases ?? 6}
                    onChange={(e) => setInsertTypeForm({ ...insertTypeForm, total_phases: Math.min(24, Math.max(1, parseInt(e.target.value, 10) || 6)) })}
                    className="h-11"
                    data-testid="input-insert-type-total-phases"
                    required
                  />
                  <p className="text-xs text-slate-500">Number of insert phases (e.g. 6 for phases 1–6, 4 for 1–4). Default 6.</p>
                </div>
              </>
            )}

            {activeTab === 'rejectionReasons' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="rejection-code">Code *</Label>
                  <Input
                    id="rejection-code"
                    value={rejectionReasonForm.code}
                    onChange={(e) => setRejectionReasonForm({ ...rejectionReasonForm, code: e.target.value })}
                    placeholder="e.g., MF, Bore, Dim"
                    className="h-11"
                    data-testid="input-rejection-reason-code"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rejection-desc">Description</Label>
                  <Input
                    id="rejection-desc"
                    value={rejectionReasonForm.description}
                    onChange={(e) => setRejectionReasonForm({ ...rejectionReasonForm, description: e.target.value })}
                    placeholder="e.g., Manufacturing defect, Bore out of spec"
                    className="h-11"
                    data-testid="input-rejection-reason-description"
                  />
                </div>
              </>
            )}

            {activeTab === 'downtimeReasons' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="downtime-code">Code *</Label>
                  <Input
                    id="downtime-code"
                    value={downtimeReasonForm.code}
                    onChange={(e) => setDowntimeReasonForm({ ...downtimeReasonForm, code: e.target.value })}
                    placeholder="e.g., BRK, SETUP, MNT"
                    className="h-11"
                    data-testid="input-downtime-reason-code"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="downtime-desc">Description</Label>
                  <Input
                    id="downtime-desc"
                    value={downtimeReasonForm.description}
                    onChange={(e) => setDowntimeReasonForm({ ...downtimeReasonForm, description: e.target.value })}
                    placeholder="e.g., Breakdown, Setup change, Maintenance"
                    className="h-11"
                    data-testid="input-downtime-reason-description"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="dialog-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="dialog-save-btn">
              {dialogMode === 'create' ? 'Create' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterDataPage;
